/**
 * Public Battle Preview — the landing page someone hits when they click a
 * shared FunnelHack share-card link `funnelhack.ai/b/:token`.
 *
 * Strategy: no auth required. Shows the original verdict + scores + top tip
 * from the sharer, then a strong CTA → /heatmap?primary=…&competitor=… that
 * pre-fills both URLs so the viewer can run their own Battle in one click.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Sword, Trophy, Warning, ArrowRight, Flame, Eye } from '@phosphor-icons/react';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/share`;


const VERDICT_MAP = {
  primary:    { color: '#10B981', icon: Trophy },
  competitor: { color: '#EF4444', icon: Warning },
  tie:        { color: '#A855F7', icon: Sword  },
};


const stripScheme = (u = '') => u.replace(/https?:\/\//, '');


export default function BattlePreview() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/${token}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.status === 404 ? 'not-found' : 'fetch'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const runYourOwn = () => {
    if (!data) return;
    // Stash the referral token client-side so we can `POST /api/share/{token}/claim`
    // the next time the viewer is authenticated. Idempotent on the backend.
    try { localStorage.setItem('fh_referral_token', token); } catch { /* private mode */ }
    const params = new URLSearchParams({
      primary: data.primary_url || '',
      competitor: data.competitor_url || '',
      via: 'share-card',
    });
    navigate(`/heatmap?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white/50 text-sm" data-testid="battle-preview-loading">
        {t('battle.loading')}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="battle-preview-error">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔗</div>
          <h1 className="text-2xl font-[Outfit] font-bold text-white mb-2">
            {error === 'not-found' ? t('battle.nf_title') : t('battle.err_title')}
          </h1>
          <p className="text-white/60 text-sm mb-6">
            {t('battle.err_sub')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#EF4444] to-[#F97316] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-all"
            data-testid="battle-preview-home-btn"
          >
            <Flame size={14} weight="bold" />
            {t('battle.go_home')}
          </Link>
        </div>
      </div>
    );
  }

  const verdictKey = VERDICT_MAP[data.verdict] ? data.verdict : 'tie';
  const verdict = VERDICT_MAP[verdictKey];
  const VerdictIcon = verdict.icon;
  const primaryScore = Math.round(data.primary_score || 0);
  const competitorScore = Math.round(data.competitor_score || 0);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="battle-preview-page">
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#3B82F6] via-[#A855F7] to-[#EF4444]" />

      <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16">
        {/* Brand */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#EF4444] via-[#F97316] to-[#F59E0B] flex items-center justify-center">
              <Flame size={18} weight="bold" className="text-white" />
            </div>
            <span className="font-[Outfit] font-bold text-lg">FunnelHack</span>
          </Link>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold flex items-center gap-2" data-testid="battle-preview-views">
            <Eye size={11} weight="bold" />
            {data.view_count} {t('battle.views')}
          </div>
        </div>

        {/* Hero */}
        <div className="text-center mb-10">
          <span className="inline-block text-[10px] uppercase tracking-[0.3em] text-[#A855F7] font-bold mb-3">
            {t('battle.kicker')}
          </span>
          <h1 className="text-4xl sm:text-5xl font-[Outfit] font-bold mb-3" data-testid="battle-preview-headline">
            {t('battle.headline')}
          </h1>
          <p className="text-white/60 text-base max-w-2xl mx-auto">
            {t('battle.sub')}
          </p>
        </div>

        {/* Verdict card */}
        <div
          className="p-7 rounded-2xl border flex items-center gap-5 mb-6"
          style={{ borderColor: `${verdict.color}55`, background: `linear-gradient(135deg, ${verdict.color}1a, transparent)` }}
          data-testid="battle-preview-verdict"
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: `${verdict.color}22`, color: verdict.color }}
          >
            <VerdictIcon size={32} weight="bold" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold mb-1">
              {t('battle.verdict_label')}
            </div>
            <div className="text-3xl font-[Outfit] font-bold" style={{ color: verdict.color }} data-testid="battle-preview-verdict-label">
              {t(`battle.verdict_${verdictKey}`)}
            </div>
          </div>
        </div>

        {/* URLs + scores */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="p-5 rounded-2xl bg-[#0A0A0A] border border-[#3B82F6]/30" data-testid="battle-preview-primary">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] font-bold mb-2">{t('battle.sharer')}</div>
            <div className="text-sm font-mono text-white/80 truncate mb-3">{stripScheme(data.primary_url)}</div>
            <div className="text-4xl font-[Outfit] font-bold text-[#3B82F6]">
              {primaryScore}
              <span className="text-sm text-white/30 font-normal ml-1">/100</span>
            </div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0A0A0A] border border-[#EF4444]/30" data-testid="battle-preview-competitor">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#EF4444] font-bold mb-2">Competitor</div>
            <div className="text-sm font-mono text-white/80 truncate mb-3">{stripScheme(data.competitor_url)}</div>
            <div className="text-4xl font-[Outfit] font-bold text-[#EF4444]">
              {competitorScore}
              <span className="text-sm text-white/30 font-normal ml-1">/100</span>
            </div>
          </div>
        </div>

        {/* Top tip */}
        {data.top_tip && (
          <div className="p-5 rounded-2xl bg-gradient-to-r from-[#F59E0B]/10 via-transparent to-transparent border border-[#F59E0B]/30 mb-10" data-testid="battle-preview-tip">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold mb-2">
              {t('battle.tip_label')}
            </div>
            <p className="text-sm text-white/85 leading-relaxed">{data.top_tip}</p>
          </div>
        )}

        {/* CTA */}
        <div className="p-8 rounded-2xl bg-gradient-to-br from-[#A855F7]/15 via-[#EF4444]/10 to-transparent border border-[#A855F7]/40 text-center">
          <h2 className="text-2xl font-[Outfit] font-bold mb-2">
            {t('battle.cta_title')}
          </h2>
          <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
            {t('battle.cta_sub')}
          </p>
          <button
            onClick={runYourOwn}
            data-testid="battle-preview-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-[#3B82F6] via-[#A855F7] to-[#EF4444] hover:opacity-90 text-white text-sm font-semibold rounded-xl transition-all"
          >
            <Sword size={15} weight="bold" />
            {t('battle.cta_btn')}
            <ArrowRight size={14} weight="bold" />
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-semibold mt-4">
            {t('battle.no_card')}
          </div>
        </div>

        {/* Social re-share row */}
        <div className="mt-8 p-5 rounded-2xl border border-white/10 bg-[#0A0A0A]" data-testid="battle-preview-share">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">{t('battle.share_label')}</div>
          <ShareButtons
            shareUrl={typeof window !== 'undefined' ? `${window.location.origin}/api/share/${token}/preview` : ''}
            verdict={data.verdict}
            primaryScore={primaryScore}
            competitorScore={competitorScore}
            primaryUrl={data.primary_url}
            competitorUrl={data.competitor_url}
          />
        </div>

        {/* Footer */}
        <div className="text-center mt-10 text-xs text-white/30">
          {t('battle.footer_pre')} <Link to="/" className="text-white/50 hover:text-white">FunnelHack</Link> · {t('battle.footer_post')}
        </div>
      </div>
    </div>
  );
}
